<template>
  <PageTemplate
    :heading="`Hei ${userProfile.name}!`"
    :loading="!loaded"
    :showError="error"
    :showLogo="true"
    errorMessage="Kunne ikke laste brukerprofil"
    data-cy="app-dashboard-mypage"
  >
    <router-link v-if="landlordsWithTaxReportIssues" :to="{ name: 'tax-report' }">
      <hl-Alert class="mb-5" variant="warning"
        >Vi har nå sendt dine inntektsopplysninger for 2020 til Skatteetaten. Du kan fremdeles rette informasjon om deg
        og dine leieforhold, men disse vil da gjelde for 2021. Klikk her for å oppdatere.</hl-Alert
      >
    </router-link>

    <hl-Toast
      v-if="isTaxReportBannerVisible"
      :title="translate('taxReportBanner.title')"
      :text="translate('taxReportBanner.content')"
      :action-label="translate('taxReportBanner.CTA')"
      action-target="_blank"
      variant="warning"
      class="mb-4"
      :action-href="taxReportBannerUrl"
    />

    <div class="grid-container">
      <WelcomeCard
        v-if="shouldShowWelcomeCard"
        @click="hideWelcomeCard"
        :subscriptionRouteName="subscriptionRouteName"
      />

      <DashboardCard heading="Dine oppgaver" iconClass="budicon-tick-sign">
        <MyPageTasks />
      </DashboardCard>

      <DashboardCard :heading="newActivityTitle" iconClass="budicon-shortcut-list">
        <MyPageNewActivities
          @activityInitiated="newActivityTitle = arguments[0].title"
          @activityDone="newActivityTitle = 'Nye aktiviteter'"
          :show-moveout-protocol-button="showMoveoutProtocolButton"
        />
      </DashboardCard>

      <DashboardCard v-if="!isProfessional" heading="Dine leieforhold" iconClass="budicon-deal">
        <Tenancies
          :tenancies="filteredTenancies"
          :shouldShowSubButton="false"
          :shouldShowRentalObjectCheckbox="false"
        />
      </DashboardCard>

      <div class="myPageGrid">
        <!-- Left side -->
        <div class="myPageFlex">
          <MyPageUnpaidRent error-message="Ingen data tilgjengelig." :show-error="dashboardDataError" />

          <MyPageUninvoicedFees v-if="hasLandlordAccess" />

          <DashboardCard heading="Husleiekalkulator" iconClass="budicon-good-trends">
            <p class="mt-5">
              Utregningen baserer seg på flere tusen nye husleiekontrakter som opprettes hver måned på Husleie.no.
            </p>
            <div class="d-flex flex-column align-items-center mt-4">
              <h4>
                13 373 kr.
              </h4>
              <p class="m-0">
                Gjennomsnittlig leiepris i Norge,
              </p>
              <p>
                1. kvartal 2026
              </p>

              <button class="btn btn-primary w-50" @click="goToRentalCalculator">
                Finn din utleiepris nå!
              </button>
            </div>
          </DashboardCard>

          <template v-if="shouldShowSubscription">
            <SmallBasicPremiumCard />
          </template>

          <DashboardCard heading="Eksklusive fordeler for våre brukere" iconClass="budicon-like">
            <p class="mt-2">
              Som bruker av Husleie.no får du rabatter og fordeler hos flere av våre samarbeidspartnere, eksempelvis
              leie av flyttebil, elektriker-tjenester, boligalarm og leie av redskaper. Ta en titt da!
            </p>

            <div class="d-flex justify-content-center mt-2">
              <button class="btn btn-secondary" @click="openAdvantagesSite">Se dine fordeler</button>
            </div>
          </DashboardCard>
        </div>
        <!-- Right side -->
        <div class="myPageFlex">
          <DashboardCard
            v-if="hasSubscription && isProfessional"
            heading="Utbetalinger"
            iconClass="budicon-good-trends"
            :loading="loadingMetrics"
            :showMessage="dashboardDataError"
            message="Ingen data tilgjengelig."
          >
            <MyPageDisbursements />
          </DashboardCard>

          <DashboardCard heading="Meldinger" iconClass="budicon-document-page">
            <span v-if="unreadMessages > 0"> Antall nye meldinger: {{ unreadMessages }} </span>
            <span v-else> Antall nye meldinger: Ingen </span>

            <div class="d-flex justify-content-center mt-4">
              <button
                class="d-flex align-items-center btn btn-sm btn-outline-secondary"
                @click="$router.push({ path: `/${landlordId}/inbox` })"
              >
                Se meldinger
                <i class="ml-1 budicon-arrow-right" />
              </button>
            </div>
          </DashboardCard>

          <DashboardCard heading="Kundeservice" iconClass="budicon-happy-chat">
            <MyPageCustomerService :has-subscription="hasSubscription" />
          </DashboardCard>

          <DashboardCard class="news" heading="Artikler fra Husleie.no" iconClass="budicon-newspaper">
            <div class="full-width-list">
              <MyPageNewsStoryList />
            </div>
            <div class="mypage-cta">
              <a
                href="https://www.husleie.no/ressurser/husleiebloggen"
                target="_blank"
                class="btn btn-sm btn-outline-secondary"
                >Flere nyheter <i class="budicon budicon-outgoing-link"></i
              ></a>
            </div>
          </DashboardCard>
        </div>
      </div>
    </div>
  </PageTemplate>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import DashboardCard from '@/components/DashboardCard';
import MyPageTasks from '@/components/mypage/MyPageTasks.vue';
import MyPageCustomerService from '@/components/mypage/MyPageCustomerService';
import MyPageDisbursements from '@/components/mypage/MyPageDisbursements';
import MyPageNewActivities from '@/components/mypage/MyPageNewActivities';
import MyPageNewsStoryList from '@/components/mypage/MyPageNewsStoryList';
import MyPageSubscriptionSeller from '@/components/mypage/MyPageSubscriptionSeller';
import MyPageUnpaidRent from '@/components/mypage/MyPageUnpaidRent';
import MyPageUninvoicedFees from '@/components/mypage/MyPageUninvoicedFees';
import SmallBasicPremiumCard from '@/components/SmallBasicPremiumCard.vue';
import Tenancies from '@/components/Tenancies.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Alert from '@modules/husleie-framework/src/components/text/Alert.vue';
import Toast from '@modules/husleie-framework/src/components/Toast.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import TagManagerOperations from '@modules/husleie-framework/src/static-classes/TagManagerOperations.class';
import WelcomeCard from '@/components/subscription/WelcomeCard.vue';
import { ProfilesLandlordApi } from '@modules/husleieapi2';

export default {
  name: 'MyPage',

  mixins: [i18nCommon],

  components: {
    DashboardCard,
    MyPageTasks,
    MyPageNewsStoryList,
    MyPageDisbursements,
    MyPageUnpaidRent,
    MyPageUninvoicedFees,
    MyPageCustomerService,
    MyPageSubscriptionSeller,
    MyPageNewActivities,
    SmallBasicPremiumCard,
    Tenancies,
    WelcomeCard,
    PageTemplate,
    'hl-Alert': Alert,
    'hl-Toast': Toast,
  },
  props: {
    showMoveoutProtocolButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      adListLoading: true,
      newActivityTitle: 'Nye aktiviteter',
      error: false,
      dashboardDataError: false,
      hasSubscription: false,
      isWelcomeCardHidden: undefined,
    };
  },

  computed: {
    ...mapState({
      loadingMetrics: state => state.reports.dashboard.loading,
    }),
    ...mapGetters({
      landlordProfiles: 'getLandlordProfilesMeta',
      userProfile: 'getUserProfile',
      condensedRentalObjects: 'getCondensedRentalObjects',
      landlordId: 'getLandlordId',
      landlordsWithTaxReportIssues: 'areThereLandlordsWithTaxReportIssues',
    }),
    ...mapGetters([
      'isTaxReportBannerVisible',
      'taxReportBannerUrl',
      'getAllTenancies',
      'getTopNotifications',
      'getLandlordProfileSubscriptions',
      'getLandlordProfileMeta',
      'getLandlordProfilesMeta',
      'areAllRentalObjectsLoaded',
      'isUserSudo',
    ]),

    shouldShowWelcomeCard() {
      if (!this.areAllRentalObjectsLoaded || this.getAllTenancies.length > 0 || this.isWelcomeCardHidden) {
        return false;
      }
      return true;
    },
    subscriptionRouteName() {
      const plans = [];

      for (const item of this.getLandlordProfileSubscriptions) {
        if (!plans.includes(item.subscription.plan)) {
          plans.push(item.subscription.plan);
        }
      }
      if (plans.includes('premium')) {
        return 'subscription_thanks';
      }
      if (plans.includes('basic')) {
        return 'subscription_premium';
      }
      return 'subscription_basic';
    },
    allLandlordIds() {
      return this.landlordProfiles.map(x => x.id);
    },
    loaded() {
      return Boolean(this.userProfile.name);
    },
    singleRentalObjectUser() {
      if (this.landlordProfiles.length > 1) {
        return false;
      }
      return this.landlordProfiles[0] && this.landlordProfiles[0].numberOfRentalObjects === 1;
    },
    shouldShowSubscription() {
      const plans = [];

      for (const item of this.getLandlordProfileSubscriptions) {
        if (!plans.includes(item.subscription.plan)) {
          plans.push(item.subscription.plan);
        }
      }
      return !plans.includes('premium');
    },
    isProfessional() {
      const landlordProfile = this.getLandlordProfileMeta(this.landlordId);
      if (!landlordProfile || !landlordProfile.meta) {
        return false;
      }
      return landlordProfile.meta.isProfessional;
    },
    unreadMessages() {
      return this.getTopNotifications.unreadMessages;
    },
    filteredTenancies() {
      if (!this.isUserSudo) {
        return this.getAllTenancies.filter(tenancy => tenancy.status !== 'shadow');
      }
      return this.getAllTenancies;
    },
    hasLandlordAccess() {
      return this.landlordProfiles && this.landlordProfiles.length > 0;
    },
  },

  watch: {
    allLandlordIds: {
      async handler(newIds) {
        this.dashboardDataError = false;

        if (newIds.length > 0) {
          try {
            await this.fetchDashboard(newIds);
          } catch (error) {
            console.error(error);
            this.dashboardDataError = true;
            this.$toast('Kunne ikke laste oversikt.', 'error');
          }

          if (newIds.length === 1) {
            try {
              // Set single landlord
              this.setLandlordId(newIds[0]);

              await this.fetchCondensedRentalObjectList(newIds[0]);
            } catch (error) {
              console.error(error);
              this.dashboardDataError = true;
              this.$toast('Kunne ikke hente utleieobjekter.', 'error');
            }
          }

          // Check if any of the users landlord IDs have connected subscriptions
          const api = new ProfilesLandlordApi();
          const checkLandlordSubscriptions = async () => {
            for (const landlordId of this.allLandlordIds) {
              try {
                const res = await api.getProfileLandlordSubscriptions(landlordId);

                if (res.length > 0) {
                  this.hasSubscription = true;
                  break;
                }
              } catch (error) {
                console.error('Error fetching landlord subscriptions for ID:', landlordId, error);
              }
            }
          };
          checkLandlordSubscriptions();
        }
      },
      immediate: true,
    },
    landlordId: {
      handler(value) {
        if (value) {
          this.fetchData({
            tenancies: ['active', 'free'],
          });
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions({
      fetchDashboard: 'reports/fetchDashboardData',
      loadUserProfile: 'loadUserProfile',
      fetchCondensedRentalObjectList: 'fetchCondensedRentalObjectList',
      fetchData: 'fetchData',
      setLandlordId: 'setLandlordId',
    }),

    removeWelcomeMessageKey() {
      window.localStorage.removeItem('showWelcomeMsg');
    },
    openAdvantagesSite() {
      window.open('https://www.husleie.no/fordeler/', '_blank');
    },
    hideWelcomeCard() {
      window.localStorage.setItem('shouldHideWelcomeCard', 'true');

      this.isWelcomeCardHidden = true;
    },
    goToRentalCalculator() {
      window.open('https://leiepris.husleie.no/?dashboard=true', '_blank');
    },
  },

  async created() {
    if (!this.userProfile.name) {
      try {
        await this.loadUserProfile();
      } catch (error) {
        this.error = true;
      }
    }

    this.isWelcomeCardHidden = window.localStorage.getItem('shouldHideWelcomeCard') === 'true' ? true : false;
  },

  mounted() {
    this.removeWelcomeMessageKey();
    /* GTM Tracking */
    TagManagerOperations.event(
      window.dataLayer,
      this.$router.currentRoute.name,
      this.$router.currentRoute.meta?.analytics
    );
  },
};
</script>

<style scoped lang="scss">
.grid-container {
  display: grid;
  row-gap: 1.25rem;
}

.myPageGrid {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 1.25rem;
}

@media (min-width: 876px) {
  .myPageGrid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 1.25rem;
  }
}

.myPageFlex {
  display: flex;
  flex-direction: column;
  row-gap: 1.25rem;
}

.full-width-list {
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
}

.mypage-cta {
  display: flex;
  justify-content: center;
  padding-top: 1.25rem;
  height: 51px;

  i {
    margin-left: 0.5rem;
  }
}
</style>
